import { COIN_PESEWAS, FUEL, describeConditions, formatCedis, shiftNet, type Conditions } from '@accra/shared';
import type { RunState } from '../../game/RunState';
import { isFast } from '../../systems/driving';
import { currentMultiplier } from '../../systems/scoring';
import { stopAt } from '../../systems/passengers/stopSequence';
import { activeRoute, tripAt, tripEnds, tripProgress } from '../../world/route';
import { el, setText } from '../components/el';

/** Score, speed, seats, cedis, next stop and the route bar. */
export class Hud {
  readonly root = el('div', 'hud');
  private readonly score = el('div', 'hud-score');
  private readonly multiplier = el('div', 'hud-mult');
  private readonly seats = el('div', 'hud-chip');
  private readonly cash = el('div', 'hud-chip cash');
  private readonly nextStop = el('div', 'hud-next');
  private readonly speed = el('div', 'hud-speed');
  private readonly metres = el('div', 'hud-chip');
  private readonly damage = el('div', 'hud-chip damage');
  private readonly rank = el('div', 'hud-chip rank');
  private readonly fuel = el('div', 'hud-chip fuel');
  private readonly conditions = el('div', 'hud-chip conditions');
  /** Wallet at the start of the shift; the chip shows it plus this shift's takings. */
  private walletAtStart = 0;
  private readonly routeFill = el('div', 'route-fill');
  private readonly bar = el('div', 'route-bar');
  private readonly lap = el('div', 'route-lap');
  /** Which trip the route bar is drawn for (legs flip order on the way back). */
  private barTrip = -1;

  constructor(parent: HTMLElement) {
    const left = el('div', 'hud-left');
    left.append(this.score, this.multiplier, this.seats, this.nextStop, this.conditions);
    const right = el('div', 'hud-right');
    right.append(this.cash, this.damage, this.fuel, this.metres, this.rank);
    const route = el('div', 'route');
    route.append(this.bar, this.lap);
    this.root.append(route, left, right, this.speed);
    parent.append(this.root);
  }

  setVisible(visible: boolean): void {
    this.root.classList.toggle('show', visible);
  }

  /** Weather and time of day, e.g. "🌧️ Rain · 🌙 Night". Hidden on a plain clear day. */
  setConditions(conditions: Conditions): void {
    const words = describeConditions(conditions);
    setText(this.conditions, words?.chip ?? '');
    this.conditions.title = words?.detail ?? '';
    this.conditions.classList.toggle('show', words !== null);
  }

  /** Live position on today's board, from the mid-shift updates. */
  setRank(rank: number | null): void {
    setText(this.rank, rank ? `🏆 #${rank} today` : '');
    this.rank.classList.toggle('show', rank !== null);
  }

  startShift(walletPesewas: number): void {
    this.walletAtStart = walletPesewas;
    this.setRank(null);
    this.barTrip = -1;
  }

  /** Legs in driving order for this trip: reversed on the way back. */
  private drawBar(trip: number): void {
    this.barTrip = trip;
    const legs = trip % 2 === 0 ? activeRoute().legs : [...activeRoute().legs].reverse();
    this.bar.replaceChildren(
      ...legs.map((leg) => {
        const tick = el('span', 'route-leg', leg.name.split(',')[0]);
        tick.style.flex = String(leg.end - leg.start);
        return tick;
      }),
      this.routeFill,
    );
  }

  update(state: RunState): void {
    const { bus, stats } = state;
    const wallet = this.walletAtStart + shiftNet(stats.faresPesewas, stats.finesPesewas, stats.repairsPesewas, stats.fuelPesewas) + stats.coins * COIN_PESEWAS;
    setText(this.damage, `🔧 ${Math.round(bus.damage)}%`);
    setText(this.fuel, `⛽ ${Math.round(state.fuel.units)}%`);
    this.fuel.classList.toggle('bad', state.fuel.units <= FUEL.lowAt);
    this.fuel.classList.toggle('filling', state.fuel.filling);
    this.damage.classList.toggle('bad', bus.damage >= 60);
    setText(this.score, Math.floor(stats.score).toLocaleString());
    setText(this.multiplier, `×${currentMultiplier(state).toFixed(1).replace('.0', '')}`);
    setText(this.seats, `🚐 ${state.passengers.length}/${bus.capacity}`);
    setText(this.cash, formatCedis(wallet));
    this.cash.classList.toggle('debt', wallet < 0);
    setText(this.metres, `${Math.floor(stats.metres)} m`);
    const point = stopAt(state.stop.seq);
    setText(this.nextStop, `Next: ${point.stop.name} · ${Math.max(0, Math.round(point.position - bus.s))} m`);
    setText(this.speed, `${Math.round(bus.speed * 3.6)} km/h${isFast(bus) ? ' · FAST' : ''}`);
    this.speed.classList.toggle('fast', isFast(bus));
    const { trip } = tripAt(bus.s);
    if (trip !== this.barTrip) this.drawBar(trip);
    this.routeFill.style.width = `${(tripProgress(bus.s) * 100).toFixed(1)}%`;
    setText(this.lap, `Trip ${trip + 1} → ${tripEnds(trip).to}`);
  }
}
